import React from "react";
import ServiceBadge from "./ServiceBadge";

interface CalendarDayCellProps {
  day: {
    date: number;
    currentMonth: boolean;
    fullDate: Date;
  };
  services: any[];
  isWorkingDay?: boolean; // Reads from API response (data.days[i].isWorkingDay)
  onDateClick: (date: Date) => void;
  onEmptySlotClick?: (time: string, dateStr: string) => void;
}

const CalendarDayCell: React.FC<CalendarDayCellProps> = ({
  day,
  services,
  isWorkingDay = true,
  onDateClick,
  onEmptySlotClick,
}) => {
  const today = new Date();

  const isToday =
    today.getDate() === day.fullDate.getDate() &&
    today.getMonth() === day.fullDate.getMonth() &&
    today.getFullYear() === day.fullDate.getFullYear();

  const handleCellClick = () => {
    if (!isWorkingDay) return; // Prevent slot creation on holiday/off day

    const dateStr = day.fullDate.toISOString().split("T")[0];

    if (services.length > 0) {
      onDateClick(day.fullDate);
    } else if (onEmptySlotClick) {
      onEmptySlotClick("09:00", dateStr);
    }
  };

  return (
    <div
      onClick={handleCellClick}
      className={`
        h-32
        border
        border-slate-200
        p-2
        transition-all
        duration-200
        ${
          !isWorkingDay
            ? "bg-rose-50/40 cursor-not-allowed"
            : "bg-white cursor-pointer hover:bg-indigo-50/40 hover:shadow-sm"
        }
      `}
    >
      {/* Header */}
      <div className="flex justify-between items-center mb-2">
        {!isWorkingDay ? (
          <span className="text-[10px] font-bold text-rose-600 bg-rose-100 px-1.5 py-0.5 rounded">
            OFF
          </span>
        ) : (
          <div />
        )}

        {!isToday ? (
          <span
            className={`
              text-sm
              font-semibold
              ${!isWorkingDay ? "text-rose-400" : day.currentMonth ? "text-gray-900" : "text-gray-400"}
            `}
          >
            {day.date}
          </span>
        ) : (
          <div
            className="
              w-8
              h-8
              rounded-full
              bg-violet-600
              text-white
              flex
              items-center
              justify-center
              text-xs
              font-semibold
            "
          >
            {day.date}
          </div>
        )}
      </div>

      {/* Services or Off Day Indicator */}
      {!isWorkingDay ? (
        <div className="h-16 flex items-center justify-center text-xs font-medium text-rose-400 italic">
          Holiday / Off
        </div>
      ) : (
        <div className="space-y-1">
          {services.slice(0, 2).map((service, idx) => (
            <ServiceBadge
              key={service.bookingId || service.id || idx}
              service={service}
            />
          ))}

          {services.length > 2 && (
            <button
              onClick={(e) => {
                e.stopPropagation();
                onDateClick(day.fullDate);
              }}
              className="
                text-xs
                text-indigo-600
                font-semibold
                hover:underline
              "
            >
              +{services.length - 2} More
            </button>
          )}
        </div>
      )}
    </div>
  );
};

export default CalendarDayCell;